import { useState, useEffect, useCallback, useRef } from 'react';
import { TaskProcessingFlowResponse } from '../types/timeline';
import { timelineApi } from '../services/timelineApi';

interface UseTaskProcessingFlowOptions {
  taskId: number | string;
  enabled?: boolean;
  onLoad?: (data: TaskProcessingFlowResponse) => void;
}

interface UseTaskProcessingFlowResult {
  data: TaskProcessingFlowResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useTaskProcessingFlow({
  taskId,
  enabled = true,
  onLoad,
}: UseTaskProcessingFlowOptions): UseTaskProcessingFlowResult {
  const [data, setData] = useState<TaskProcessingFlowResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const onLoadRef = useRef(onLoad);

  useEffect(() => {
    onLoadRef.current = onLoad;
  }, [onLoad]);

  const fetchFlow = useCallback(async () => {
    if (!taskId || !enabled) return;

    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const response = await timelineApi.getTaskProcessingFlow(taskId);
      if (reqId === requestIdRef.current) {
        setData(response);
        onLoadRef.current?.(response);
      }
    } catch (err) {
      if (reqId === requestIdRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load processing flow');
      }
    } finally {
      if (reqId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [taskId, enabled]);

  useEffect(() => {
    if (taskId && enabled) {
      fetchFlow();
    }
  }, [taskId, enabled, fetchFlow]);

  return { data, loading, error, refresh: fetchFlow };
}
